import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const hasAny = (params, keys) => keys.some(key => params.has(key));

// QueryString values are all strings, so we have a standardized 'guessing' for them for our limited types
const COERCE_BY_DATA_TYPE = {
  string: raw => raw,
  number: raw => Number(raw),
  boolean: raw => raw === 'true',
};

// A dataType declared on the matching filter option wins. Failing that the default value's own type
// is the best guess available, which an empty array cannot give, so array members stay strings
// unless their filter option says otherwise.
const resolveCoerce = (key, defaultValue, filterOptions) => {
  const dataType =
    filterOptions?.find(option => option.key === key)?.dataType ??
    (Array.isArray(defaultValue) ? 'string' : typeof defaultValue);

  return { dataType, coerce: COERCE_BY_DATA_TYPE[dataType] ?? COERCE_BY_DATA_TYPE.string };
};

const writeFilterParams = (params, filters) => {
  const next = new URLSearchParams(params);
  const keys = Object.keys(filters || {});

  keys.forEach(key => {
    next.delete(key);

    (Array.isArray(filters[key]) ? filters[key] : [filters[key]])
      .filter(
        // Persistable filters limited to string, number, bool (dates/complex types would require new process)
        value =>
          (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') &&
          value !== '',
      )
      .forEach(value => next.append(key, String(value)));
  });

  // Cleared filters would not be in URL, so if we clear all filters we just put one empty param
  if (keys.length && !hasAny(next, keys)) {
    next.set(keys[0], '');
  }

  return next;
};

// Checks if there are any filter params from an object
export const hasFilterParams = filterShape =>
  hasAny(new URLSearchParams(window.location.search), Object.keys(filterShape));

// Uses a filter shape object to read current values -- assumes it is a default. Passing the page's
// filterOptions lets a filter declare its dataType instead of relying on the guess above
export const readFilterParams = (filterShape, filterOptions) => {
  const params = new URLSearchParams(window.location.search);
  const keys = Object.keys(filterShape);

  if (!hasAny(params, keys)) {
    // null when nothing was found for this shape
    return null;
  }

  return keys.reduce((acc, key) => {
    const defaultValue = filterShape[key];
    const { dataType, coerce } = resolveCoerce(key, defaultValue, filterOptions);
    const values = params.getAll(key).filter(Boolean);

    if (Array.isArray(defaultValue)) {
      acc[key] = values.map(value => coerce(value));
    } else if (values.length) {
      acc[key] = coerce(values[0]);
    } else {
      // Missing if boolean is null to align with list action bar
      acc[key] = dataType === 'boolean' ? null : '';
    }

    return acc;
  }, {});
};

// Takes a filter shape and writes it to URL
export const useFilterParams = filters => {
  const navigate = useNavigate();
  const isInitialRender = useRef(true);

  useEffect(() => {
    // With ref, we only write params on subsequent renders
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    const current = new URLSearchParams(window.location.search);
    const search = writeFilterParams(current, filters).toString();

    if (search !== current.toString()) {
      navigate({ search }, { replace: true });
    }
  }, [filters, navigate]);
};
